import type { ModelProps, ModelRegistry } from '../types';

function CactusSaguaro({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Main body */}
      <mesh position={[0, 0.9, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.18, 1.8, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#2E8B57' : '#1a5a3a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Ridges on main body */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={i} position={[Math.cos(i * Math.PI / 3) * 0.16, 0.9, Math.sin(i * Math.PI / 3) * 0.16]} castShadow>
          <boxGeometry args={[0.02, 1.7, 0.02]} />
          <meshStandardMaterial color={world === 'real' ? '#228B22' : '#155a22'} roughness={0.8} />
        </mesh>
      ))}
      {/* Left arm */}
      <group position={[-0.15, 0.8, 0]} rotation={[0, 0, -0.3]}>
        <mesh position={[0, 0.15, 0]}>
          <cylinderGeometry args={[0.08, 0.1, 0.3, 8]} />
          <meshStandardMaterial color={world === 'real' ? '#2E8B57' : '#1a5a3a'} roughness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
        <mesh position={[-0.15, 0.35, 0]} rotation={[0, 0, 0.5]}>
          <cylinderGeometry args={[0.06, 0.08, 0.4, 8]} />
          <meshStandardMaterial color={world === 'real' ? '#2E8B57' : '#1a5a3a'} roughness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
      </group>
      {/* Right arm */}
      <group position={[0.15, 1.1, 0]} rotation={[0, 0, 0.4]}>
        <mesh position={[0, 0.12, 0]}>
          <cylinderGeometry args={[0.07, 0.09, 0.25, 8]} />
          <meshStandardMaterial color={world === 'real' ? '#2E8B57' : '#1a5a3a'} roughness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
        <mesh position={[0.12, 0.3, 0]} rotation={[0, 0, -0.5]}>
          <cylinderGeometry args={[0.05, 0.07, 0.35, 8]} />
          <meshStandardMaterial color={world === 'real' ? '#2E8B57' : '#1a5a3a'} roughness={0.7} transparent={isOccluding} opacity={opacity} />
        </mesh>
      </group>
      {/* Flower on top */}
      <mesh position={[0, 1.85, 0]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFD700' : '#DDA0DD'}
          emissive={world === 'spectral' ? '#DDA0DD' : undefined}
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}

function CactusBarrel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.25, 0]} castShadow>
        <sphereGeometry args={[0.3, 16, 12]} />
        <meshStandardMaterial
          color={world === 'real' ? '#3CB371' : '#2a6a4a'}
          roughness={0.6}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Spines */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i * Math.PI * 2) / 8;
        return (
          <group key={i}>
            {[0, 1, 2].map((j) => (
              <mesh key={j} position={[Math.cos(angle) * 0.28, 0.15 + j * 0.12, Math.sin(angle) * 0.28]} rotation={[0, angle, Math.PI / 4]}>
                <coneGeometry args={[0.01, 0.08, 4]} />
                <meshStandardMaterial color={world === 'real' ? '#F5DEB3' : '#c0a080'} />
              </mesh>
            ))}
          </group>
        );
      })}
      {/* Flower crown */}
      <mesh position={[0, 0.5, 0]}>
        <torusGeometry args={[0.08, 0.03, 6, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#FF69B4' : '#BA55D3'} />
      </mesh>
    </group>
  );
}

function CactusPrickly({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Base pad */}
      <mesh position={[0, 0.2, 0]} rotation={[0.2, 0, 0]} castShadow>
        <capsuleGeometry args={[0.15, 0.1, 4, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#6B8E23' : '#4a6a3a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Upper pads */}
      {[[-0.12, 0.4, 0.05, 0.3], [0.1, 0.45, -0.03, -0.2], [0, 0.6, 0.08, 0.1]].map(([x, y, z, rot], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[0.3, rot, 0.1 * (i - 1)]} castShadow>
          <capsuleGeometry args={[0.1 - i * 0.02, 0.08, 4, 8]} />
          <meshStandardMaterial
            color={world === 'real' ? '#6B8E23' : '#4a6a3a'}
            roughness={0.7}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Prickly fruits */}
      {[[0.15, 0.55, 0], [-0.08, 0.65, 0.05]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[0.04, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#DC143C' : '#8B008B'} />
        </mesh>
      ))}
    </group>
  );
}

function DatePalm({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Tall thin trunk */}
      <mesh position={[0, 1, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 2, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#A0522D' : '#6a4a3a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Trunk texture rings */}
      {[0.3, 0.6, 0.9, 1.2, 1.5].map((y, i) => (
        <mesh key={i} position={[0, y, 0]}>
          <torusGeometry args={[0.11 - i * 0.005, 0.015, 6, 12]} />
          <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a3020'} roughness={0.9} />
        </mesh>
      ))}
      {/* Fronds - longer and thinner */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
        const angle = (i * Math.PI * 2) / 10;
        return (
          <group key={i} position={[0, 2, 0]} rotation={[0.5 + Math.sin(i) * 0.2, angle, 0]}>
            <mesh position={[0, 0, 0.6]}>
              <coneGeometry args={[0.08, 1.2, 3]} />
              <meshStandardMaterial
                color={world === 'real' ? '#6B8E23' : '#4a6a3a'}
                roughness={0.7}
                side={2}
                transparent={isOccluding}
                opacity={opacity}
              />
            </mesh>
          </group>
        );
      })}
      {/* Date clusters */}
      <group position={[0, 1.9, 0.15]}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh key={i} position={[Math.sin(i * 1.2) * 0.08, -i * 0.04, Math.cos(i * 1.2) * 0.05]}>
            <sphereGeometry args={[0.03, 6, 6]} />
            <meshStandardMaterial color={world === 'real' ? '#8B4513' : '#5a3020'} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function Tumbleweed({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.2, 0]} castShadow>
        <icosahedronGeometry args={[0.25, 1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#D2B48C' : '#a08060'}
          roughness={1}
          wireframe
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Inner structure */}
      <mesh position={[0, 0.2, 0]}>
        <icosahedronGeometry args={[0.15, 0]} />
        <meshStandardMaterial
          color={world === 'real' ? '#C4A484' : '#907050'}
          roughness={1}
          wireframe
        />
      </mesh>
    </group>
  );
}

function DesertGrass({ world }: ModelProps) {
  return (
    <group>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const angle = (i * Math.PI * 2) / 7 + Math.sin(i) * 0.3;
        const height = 0.2 + Math.sin(i * 2) * 0.1;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.08, height / 2, Math.sin(angle) * 0.08]} rotation={[0.2 * Math.sin(i), 0, 0.1 * Math.cos(i)]}>
            <coneGeometry args={[0.015, height, 4]} />
            <meshStandardMaterial
              color={world === 'real' ? '#BDB76B' : '#8a8a5a'}
              roughness={0.9}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export const desertModels: ModelRegistry = {
  cactus_saguaro: CactusSaguaro,
  cactus_barrel: CactusBarrel,
  cactus_prickly: CactusPrickly,
  date_palm: DatePalm,
  tumbleweed: Tumbleweed,
  desert_grass: DesertGrass,
};
